import { useOnboardingContext } from '@/contexts/OnboardingContext'
import { useProducts, PRODUCTS_QUERY_KEY } from '@/hooks/useProducts'
import { useDocuments } from '@/hooks/useDocuments'
import { useChecklists, CHECKLISTS_QUERY_KEY } from '@/hooks/useChecklists'
import { useTeamMembers } from '@/hooks/useTeam'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/hooks/useAuth'
import { useQueryClient } from '@tanstack/react-query'

/**
 * Main hook for accessing onboarding state and actions
 */
export function useOnboarding() {
  const context = useOnboardingContext()
  const { data: products = [] } = useProducts()
  const { data: documents = [] } = useDocuments()
  const { data: checklists = [] } = useChecklists()
  const { data: members = [] } = useTeamMembers()
  const { profile } = useAuth()

  // P1 #8: Check if org was actually configured (name differs from default pattern)
  const [orgConfigured, setOrgConfigured] = useState(false)
  useEffect(() => {
    if (!profile?.organization_id) return
    let cancelled = false
    async function checkOrg() {
      try {
        const { data } = await supabase
          .from('organizations')
          .select('name')
          .eq('id', profile!.organization_id!)
          .single()
        if (cancelled) return
        if (data?.name && !data.name.endsWith("'s Organization")) {
          setOrgConfigured(true)
        } else {
          setOrgConfigured(false)
        }
      } catch (err) {
        if (!cancelled) {
          const supaErr = err as { code?: string; message?: string }
          console.error('Failed to check organization config:', supaErr.code, supaErr.message)
        }
      }
    }
    checkOrg()
    return () => { cancelled = true }
  }, [profile?.organization_id])

  // Auto-detect completed steps based on actual data
  const autoDetectedSteps = useMemo(() => {
    const detected: string[] = []

    // Organization: only mark complete if org name was customized
    if (orgConfigured) detected.push('organization')

    if (products.length > 0) detected.push('products')
    if (documents.length > 0) detected.push('documents')
    if (checklists.length > 0) detected.push('checklists')

    // Evidence step: check if any checklist has status beyond 'not_started'
    const hasStartedChecklists = checklists.some(c =>
      c.status === 'in_progress' || c.status === 'completed' || c.status === 'approved'
    )
    if (hasStartedChecklists) detected.push('evidence')

    // Team: detect if there are other members (beyond the founder)
    if (members.length > 1) detected.push('team')

    return detected
  }, [products, documents, checklists, members, orgConfigured])

  // Combined completed steps (manual + auto-detected)
  const allCompletedSteps = useMemo(() => {
    return [...new Set([...context.completedSteps, ...autoDetectedSteps])]
  }, [context.completedSteps, autoDetectedSteps])

  // Enhanced steps with auto-detection
  const enhancedSteps = useMemo(() => {
    return context.steps.map(step => ({
      ...step,
      completed: allCompletedSteps.includes(step.id),
    }))
  }, [context.steps, allCompletedSteps])

  // Recalculate completion percentage with auto-detection
  const completionPercentage = useMemo(() => {
    if (enhancedSteps.length === 0) return 0
    const completed = enhancedSteps.filter(s => s.completed).length
    return Math.round((completed / enhancedSteps.length) * 100)
  }, [enhancedSteps])

  // Recalculate isOnboardingComplete with auto-detection
  const isOnboardingComplete = useMemo(() => {
    return enhancedSteps.length > 0 && enhancedSteps.every(s => s.completed)
  }, [enhancedSteps])

  return {
    ...context,
    steps: enhancedSteps,
    completedSteps: allCompletedSteps,
    completionPercentage,
    isOnboardingComplete,
    
    // Data counts for wizard display
    hasProducts: products.length > 0,
    hasDocuments: documents.length > 0,
    hasChecklists: checklists.length > 0,
    productCount: products.length,
    documentCount: documents.length,
    checklistCount: checklists.length,
  }
}

/**
 * Hook for creating quick start sample data
 */
export function useQuickStart() {
  const { profile } = useAuth()
  const { markQuickStartUsed, completeStep } = useOnboardingContext()
  const queryClient = useQueryClient()

  const createSampleData = useCallback(async () => {
    if (!profile?.organization_id) {
      throw new Error('No organization found')
    }

    const orgId = profile.organization_id

    try {
      // Create sample product
      const { data: product, error: productError } = await supabase
        .from('products')
        .insert({
          organization_id: orgId,
          name: 'Sample Product - Widget Pro',
          sku: 'SAMPLE-001',
          description: 'This is a sample product to help you explore LogiDocs Certify. Feel free to edit or delete it.',
          status: 'in_progress',
          category: 'Electronics',
        })
        .select()
        .single()

      if (productError) throw productError

      // Create sample checklist from a template if available
      const { data: template } = await supabase
        .from('checklist_templates')
        .select('id, name, type')
        .limit(1)
        .maybeSingle()

      if (template) {
        const { error: checklistError } = await supabase
          .from('checklists')
          .insert({
            organization_id: orgId,
            product_id: product.id,
            template_id: template.id,
            name: template.name ?? 'Sample Compliance Checklist - Widget Pro',
            type: template.type,
            status: 'not_started',
          })

        if (checklistError) console.warn('Could not create sample checklist:', checklistError)
      }

      // Invalidate caches so UI reflects new data
      await queryClient.invalidateQueries({ queryKey: PRODUCTS_QUERY_KEY })
      await queryClient.invalidateQueries({ queryKey: CHECKLISTS_QUERY_KEY })

      // Mark quick start as used
      await markQuickStartUsed()
      await completeStep('products')

      return { product }
    } catch (error) {
      console.error('Failed to create sample data:', error)
      throw error
    }
  }, [profile?.organization_id, markQuickStartUsed, completeStep, queryClient])

  return { createSampleData }
}

/**
 * Hook for feature tour management
 */
export function useFeatureTour(tourId: string) {
  const { featureTours, markTourShown } = useOnboardingContext()
  
  const tour = featureTours.find(t => t.id === tourId)
  const shouldShow = tour && !tour.shown
  
  const dismiss = useCallback(async () => {
    await markTourShown(tourId)
  }, [tourId, markTourShown])

  return {
    tour,
    shouldShow,
    dismiss,
  }
}

/**
 * Hook for checking if a specific feature tour should be shown
 */
export function useShouldShowTour(tourId: string): boolean {
  const { featureTours, isLoading } = useOnboardingContext()
  
  if (isLoading) return false
  
  const tour = featureTours.find(t => t.id === tourId)
  return tour ? !tour.shown : false
}
